import { Link, useLocation } from "@tanstack/react-router";
import { HeadingAccent } from "./HeadingAccent";
import type { CSSProperties } from "react";

type PageHeroProps = {
  title: string;
  backgroundImage?: string;
  compact?: boolean;
};

export function PageHero({ title, backgroundImage, compact = false }: PageHeroProps) {
  const location = useLocation();
  const crumbs = getBreadcrumbs(location.pathname, title);
  const heroStyle = backgroundImage
    ? ({ "--page-hero-image": `url("${backgroundImage}")` } as CSSProperties)
    : undefined;

  return (
    <>
      <header
        className={`page-header site-page-hero inner-page-hero${compact ? " inner-page-hero--compact" : ""}`}
        style={heroStyle}
      >
        <div className="container mx-auto max-w-7xl px-6 lg:px-10">
          <div className="row align-items-center justify-content-center">
            <div className="col-lg-11">
              <div className="page-header-box text-center">
                <h1
                  className={`inner-page-title font-display${compact ? " inner-page-title--compact" : ""}`}
                >
                  <HeadingAccent>{title}</HeadingAccent>
                </h1>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="page-breadcrumb-bar border-y border-border bg-background/95">
        <div className="container mx-auto max-w-7xl px-6 py-4 lg:px-10">
          <nav className="page-breadcrumb" aria-label="Breadcrumb">
            <ol className="breadcrumb">
              {crumbs.map((crumb, index) => (
                <li
                  key={`${crumb.label}-${index}`}
                  className={`breadcrumb-item${index === crumbs.length - 1 ? " active" : ""}`}
                  aria-current={index === crumbs.length - 1 ? "page" : undefined}
                >
                  {index > 0 && (
                    <span className="page-breadcrumb-separator" aria-hidden="true">
                      /
                    </span>
                  )}
                  {index === crumbs.length - 1 ? (
                    <span>{crumb.label}</span>
                  ) : (
                    <BreadcrumbLink href={crumb.href}>{crumb.label}</BreadcrumbLink>
                  )}
                </li>
              ))}
            </ol>
          </nav>
        </div>
      </div>
    </>
  );
}

function BreadcrumbLink({ href, children }: { href: string; children: string }) {
  switch (href) {
    case "/":
      return <Link to="/">{children}</Link>;
    case "/blog":
      return <Link to="/blog">{children}</Link>;
    case "/seo":
      return <Link to="/seo">{children}</Link>;
    case "/areas":
      return <Link to="/areas">{children}</Link>;
    case "/services":
      return <Link to="/services">{children}</Link>;
    case "/contact":
      return <Link to="/contact">{children}</Link>;
    default:
      return <a href={href}>{children}</a>;
  }
}

function getBreadcrumbs(pathname: string, title: string) {
  const cleanTitle = title.replace(/\s*\|\s*Phil Carr SEO Consultant\s*$/i, "").trim();
  const segments = pathname.split("/").filter(Boolean);

  if (segments.length === 0) {
    return [{ label: "Home", href: "/" }];
  }

  const crumbs = [{ label: "Home", href: "/" }];
  const parentPath = segments[0] ? `/${segments[0]}` : "/";

  if (segments.length > 1) {
    crumbs.push({ label: labelFromSegment(segments[0]), href: parentPath });
  }

  const currentLabel =
    segments[0] === "blog" && segments.length > 1
      ? cleanTitle
      : labelFromSegment(segments[segments.length - 1]);
  crumbs.push({ label: currentLabel, href: pathname });
  return crumbs;
}

function labelFromSegment(segment: string) {
  const words: Record<string, string> = {
    seo: "SEO",
    ai: "AI",
    faqs: "FAQs",
    wordpress: "WordPress",
    vs: "vs",
  };
  return segment
    .split("-")
    .map((word) => words[word] ?? word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
